import React, { useState, useEffect } from 'react';

import {
  Box,
  Heading,
  VStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  List,
  ListItem,
  HStack,
  ListIcon,
  Tag,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { WarningIcon, CheckCircleIcon } from '@chakra-ui/icons';

// URL del tuo backend Express (NOTA: cambia se il tuo server non è su localhost:3000)
const API_URL = `http://${process.env.REACT_APP_API_BASE_URL}/api/segnalazioni/blacklist?rischio=MEDIO_E_ALTO&limit=50`;

// Mappa per colorare lo sfondo in base al rischio
const getRiskColor = (risk) => {
  switch (risk) {
    case 'ALTO':
      return 'red.100';
    case 'MEDIO':
      return 'orange.100';
    default:
      return 'gray.100';
  }
};

/**
 * Componente per visualizzare la lista pubblica dei numeri ad Alto/Medio Rischio.
 * Interagisce con GET /api/segnalazioni/blacklist
 */
const Blacklist = () => {
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colori di sfondo dinamici
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    const fetchBlacklist = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error(`Errore di rete: ${response.statusText}`);
        }
        const data = await response.json();
        setBlacklist(data);
        setLoading(false);
      } catch (err) {
        console.error("Errore nel recupero della Blacklist:", err);
        setError("Impossibile connettersi al server API per la Blacklist.");
        setLoading(false);
      }
    };

    fetchBlacklist();
  }, []);

  if (loading) {
    return (
      <VStack spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text>Caricamento della Blacklist...</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error} Assicurati che il tuo server Express sia in esecuzione su **http://localhost:3000**.
      </Alert>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={4} textAlign="center" color="red.600">
        Blacklist Pubblica (Numeri a Rischio)
      </Heading>
      <Text mb={6} textAlign="center" color="gray.600">
        Questa lista mostra i numeri classificati come **ALTO** o **MEDIO** rischio, ordinati per gravità.
      </Text>

      {blacklist.length === 0 ? (
        <Alert status="success" borderRadius="md" variant="subtle">
          <AlertIcon />
          Nessun numero attualmente nella Blacklist. Il sistema è pulito!
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {blacklist.map((item, index) => (
            <Box
              key={index}
              p={4}
              bg={cardBg}
              borderRadius="lg"
              boxShadow="lg"
              borderLeft="8px solid"
              borderColor={getRiskColor(item.classificazione).replace('.100', '.500')}
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-2px)', boxShadow: '2xl' }}
            >
              <VStack align="start" spacing={1}>
                <HStack>
                  <ListIcon 
                    as={item.classificazione === 'ALTO' ? WarningIcon : CheckCircleIcon} 
                    color={item.classificazione === 'ALTO' ? 'red.500' : 'orange.500'} 
                  />
                  <Text fontSize="xl" fontWeight="bold">
                    {item.numero}
                  </Text>
                </HStack>
                
                <Tag 
                  size="md" 
                  colorScheme={item.classificazione === 'ALTO' ? 'red' : 'orange'}
                  variant="solid"
                >
                  Rischio: {item.classificazione}
                </Tag>
                
                {/* Nota: I tag popolari non sono inclusi nella rotta Blacklist che hai fornito
                     quindi questa sezione è omessa per compatibilità, ma potresti aggiungerla 
                     al tuo backend per arricchire la risposta. 
                */}
                
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default Blacklist;